/**
 * CableRenderer - Cable rendering and visual state
 * 
 * Handles all cable rendering operations:
 * - Cable path calculation (bezier/orthogonal/straight)
 * - Rendering all cables to SVG
 * - Cable selection visuals
 * - Port connected state visuals
 * - Bundled cable rendering for folded groups
 * 
 * Subscribes to EventBus events:
 * - CONNECTION_ADDED: Re-render cables and update port states
 * - CONNECTION_DELETED: Re-render cables and update port states
 * - DEVICE_UPDATED: Re-render cables (layout changes affect cable positions)
 */

import { showToast } from './ToastController.js';
import { eventBus, EVENTS } from '../services/EventBus.js';

// ============= STATE =============

// Module dependencies (injected via init)
let cablesSvg = null;
let canvas = null;
let instanceModel = null;
let connectionModel = null;
let definitionModel = null;
let getViewportFunc = null;
let getTemplateForDeviceFunc = null;
let clearDeviceSelectionFunc = null;
let updateSelectionButtonsFunc = null;
let renderLibraryFunc = null;
let state = null; // Reference to app state for selectedConnectionId

// Debug config reference
let debugConfig = null;

// Effective cable style getter (respects patch overrides)
let getEffectiveCableStyleFunc = null;

// Default cable colors by signal type (must match CSS .cable-* classes)
const DEFAULT_CABLE_COLORS = {
    'audio': '#00d9ff',
    'midi': '#ff9f1c',
    'midi-rtp': '#f59e0b',
    'midi-usb-host': '#eab308',
    'midi-usb-device': '#facc15',
    'cv': '#a855f7',
    'gate': '#22c55e',
    'clock': '#f472b6',
    'media': '#ff9f1c',
    'insert': '#e040fb'
};

// ============= INIT =============

/**
 * Initialize the cable renderer with dependencies
 * @param {Object} deps - Dependencies
 */
export function initCableRenderer(deps) {
    cablesSvg = deps.cablesSvg;
    canvas = deps.canvas;
    instanceModel = deps.instanceModel;
    connectionModel = deps.connectionModel;
    definitionModel = deps.definitionModel;
    getViewportFunc = deps.getViewport;
    getTemplateForDeviceFunc = deps.getTemplateForDevice;
    clearDeviceSelectionFunc = deps.clearDeviceSelection;
    updateSelectionButtonsFunc = deps.updateSelectionButtons;
    renderLibraryFunc = deps.renderLibrary;
    state = deps.state;
    debugConfig = deps.debugConfig;
    getEffectiveCableStyleFunc = deps.getEffectiveCableStyle || null;
    
    // Subscribe to events for automatic cable/port updates
    // This allows other modules to emit events without direct dependencies on CableRenderer
    eventBus.on(EVENTS.CONNECTION_ADDED, () => {
        renderAllCables();
        updateConnectedPorts();
    });
    
    eventBus.on(EVENTS.CONNECTION_DELETED, () => {
        renderAllCables();
        updateConnectedPorts();
    });
    
    eventBus.on(EVENTS.DEVICE_UPDATED, () => {
        // Layout changes may affect cable positions
        renderAllCables();
        updateConnectedPorts();
    });
    
    eventBus.on(EVENTS.DEVICE_DELETED, () => {
        // Device deletion also removes its connections
        renderAllCables();
        updateConnectedPorts();
    });
}

// ============= HELPER FUNCTIONS =============

/**
 * Get template for a device instance
 */
function getTemplateForDevice(device) {
    return getTemplateForDeviceFunc(device);
}

/**
 * Get group index from template by reference (UUID or index)
 */
function getGroupIndex(template, groupRef) {
    if (typeof groupRef === 'number') {
        return groupRef;
    }
    if (typeof groupRef === 'string' && /^\d+$/.test(groupRef)) {
        return parseInt(groupRef, 10);
    }
    // Legacy UUID lookup
    const idx = template.portGroups.findIndex(g => g.id === groupRef);
    return idx >= 0 ? idx : 0;
}

/**
 * Get custom color for a port group from device layout
 */
export function getGroupColorFromDevice(device, groupId) {
    const layout = device.groupLayout?.[groupId];
    if (typeof layout === 'object' && layout.color) {
        return layout.color;
    }
    return null;
}

/**
 * Apply subtle shade variation to a color based on index
 * Creates visual distinction between cables in the same group
 * @param {string} color - CSS color (hex, rgb, hsl, or named)
 * @param {number} index - Cable index within the group (0-based)
 * @returns {string} Varied color as HSL string
 */
function varyShade(color, index) {
    // Parse color to RGB
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = color;
    const parsed = ctx.fillStyle; // Browser normalizes to rgb/rgba
    
    let r, g, b;
    if (parsed.startsWith('#')) {
        // Hex color
        const hex = parsed.slice(1);
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
    } else {
        // rgb(r, g, b) format
        const match = parsed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return color; // Fallback if parsing fails
        r = parseInt(match[1]);
        g = parseInt(match[2]);
        b = parseInt(match[3]);
    }
    
    // Convert RGB to HSL
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
        }
    }
    
    // Apply subtle variation: alternate between lighter and darker
    // Range: ±8% lightness, cycling through variations
    const variation = ((index % 5) - 2) * 0.04; // -0.08, -0.04, 0, +0.04, +0.08
    const newL = Math.max(0.15, Math.min(0.85, l + variation));
    
    // Also add tiny saturation variation for more distinction
    const satVariation = ((index % 3) - 1) * 0.05; // -0.05, 0, +0.05
    const newS = Math.max(0.3, Math.min(1, s + satVariation));
    
    return `hsl(${Math.round(h * 360)}, ${Math.round(newS * 100)}%, ${Math.round(newL * 100)}%)`;
}

/**
 * Get the foldKey for a specific port in a group (handles subgroups)
 * The foldKey is based on the group's UUID (not numeric index), matching how fold state is stored
 */
export function getFoldKeyForPort(device, groupId, portIndex) {
    // Get the group's UUID from the template
    const template = getTemplateForDevice(device);
    const group = template?.portGroups?.[groupId];
    const groupUUID = group?.id || String(groupId);
    
    const layout = device.groupLayout;
    if (!layout) return groupUUID;
    
    // Check for split using the UUID key
    const groupLayout = layout[groupUUID];
    const splitSize = typeof groupLayout === 'object' ? groupLayout.splitSize : null;
    
    if (!splitSize) {
        return groupUUID;
    }
    
    // Calculate which subgroup this port belongs to
    const subgroupOffset = Math.floor((portIndex - 1) / splitSize) * splitSize + 1;
    return `${groupUUID}:${subgroupOffset}`;
}

/**
 * Check if a specific port's subgroup is folded
 */
export function isPortFolded(device, groupId, portIndex) {
    const layout = device.groupLayout;
    if (!layout) return false;
    
    const foldKey = getFoldKeyForPort(device, groupId, portIndex);
    return typeof layout[foldKey] === 'object' && layout[foldKey].folded;
}

/**
 * Get source group index from connection (handles legacy format)
 */
export function getConnectionSourceGroup(conn) {
    // New format uses sourceGroupIndex (number)
    if (typeof conn.sourceGroupIndex === 'number') {
        return conn.sourceGroupIndex;
    }
    // Legacy format uses sourceGroupId (UUID string)
    if (conn.sourceGroupId) {
        const device = instanceModel.getById(conn.sourceDeviceId);
        if (device) {
            const template = getTemplateForDevice(device);
            if (template) {
                return getGroupIndex(template, conn.sourceGroupId);
            }
        }
    }
    return 0;
}

/**
 * Get target group index from connection (handles legacy format)
 */
export function getConnectionTargetGroup(conn) {
    // New format uses targetGroupIndex (number)
    if (typeof conn.targetGroupIndex === 'number') {
        return conn.targetGroupIndex;
    }
    // Legacy format uses targetGroupId (UUID string)
    if (conn.targetGroupId) {
        const device = instanceModel.getById(conn.targetDeviceId);
        if (device) {
            const template = getTemplateForDevice(device);
            if (template) {
                return getGroupIndex(template, conn.targetGroupId);
            }
        }
    }
    return 0;
}

// ============= PATH CALCULATION =============

/**
 * Create SVG path for a cable between two points
 * Supports curvy (bezier), orthogonal, and straight styles
 */
export function createCablePath(x1, y1, x2, y2, side1 = 'right', side2 = 'left', overrideColor = null) {
    // Use patch-specific style if available, otherwise fall back to debug config
    const style = getEffectiveCableStyleFunc ? getEffectiveCableStyleFunc() : (debugConfig?.cableStyle || 'curvy');
    
    // Calculate the direction vector for each side
    function getDirection(side) {
        switch(side) {
            case 'top': return { dx: 0, dy: -1 };
            case 'bottom': return { dx: 0, dy: 1 };
            case 'left': return { dx: -1, dy: 0 };
            case 'right': 
            default: return { dx: 1, dy: 0 };
        }
    }
    
    const dir1 = getDirection(side1);
    const dir2 = getDirection(side2);
    
    if (style === 'straight') {
        // STRAIGHT: Direct line with small curves at source/target only
        const curveRadius = 8;
        const p1x = x1 + dir1.dx * curveRadius;
        const p1y = y1 + dir1.dy * curveRadius;
        const p2x = x2 + dir2.dx * curveRadius;
        const p2y = y2 + dir2.dy * curveRadius;
        
        // Simple quadratic curve at each end for a slight bend
        const midX = (p1x + p2x) / 2;
        const midY = (p1y + p2y) / 2;
        
        return `M ${x1} ${y1} Q ${p1x} ${p1y}, ${midX} ${midY} Q ${p2x} ${p2y}, ${x2} ${y2}`;
    }
    
    if (style === 'orthogonal') {
        // ORTHOGONAL: Straight H/V lines with rounded corners
        const cornerRadius = 12;
        const offset = 20; // Initial offset from connector
        
        // Start and end points after initial offset
        const p1x = x1 + dir1.dx * offset;
        const p1y = y1 + dir1.dy * offset;
        const p2x = x2 + dir2.dx * offset;
        const p2y = y2 + dir2.dy * offset;
        
        // Calculate midpoints for orthogonal routing
        // Strategy: go out from source, then horizontal/vertical to align, then to target
        const isHorizontal1 = (side1 === 'left' || side1 === 'right');
        const isHorizontal2 = (side2 === 'left' || side2 === 'right');
        
        let path = `M ${x1} ${y1} L ${p1x} ${p1y}`;
        
        if (isHorizontal1 === isHorizontal2) {
            // Both horizontal or both vertical - need two turns
            if (isHorizontal1) {
                // Horizontal ports: go out, then vertical, then horizontal to target
                const midX = (p1x + p2x) / 2;
                
                // First corner
                const c1x = midX;
                const c1y = p1y;
                // Second corner  
                const c2x = midX;
                const c2y = p2y;
                
                path += ` L ${c1x - cornerRadius * Math.sign(c1x - p1x)} ${p1y}`;
                path += ` Q ${c1x} ${c1y}, ${c1x} ${c1y + cornerRadius * Math.sign(c2y - c1y)}`;
                path += ` L ${c2x} ${c2y - cornerRadius * Math.sign(c2y - c1y)}`;
                path += ` Q ${c2x} ${c2y}, ${c2x + cornerRadius * Math.sign(p2x - c2x)} ${c2y}`;
            } else {
                // Vertical ports: go out, then horizontal, then vertical to target
                const midY = (p1y + p2y) / 2;
                
                const c1x = p1x;
                const c1y = midY;
                const c2x = p2x;
                const c2y = midY;
                
                path += ` L ${p1x} ${c1y - cornerRadius * Math.sign(c1y - p1y)}`;
                path += ` Q ${c1x} ${c1y}, ${c1x + cornerRadius * Math.sign(c2x - c1x)} ${c1y}`;
                path += ` L ${c2x - cornerRadius * Math.sign(c2x - c1x)} ${c2y}`;
                path += ` Q ${c2x} ${c2y}, ${c2x} ${c2y + cornerRadius * Math.sign(p2y - c2y)}`;
            }
        } else {
            // One horizontal, one vertical - single corner
            const cornerX = isHorizontal1 ? p2x : p1x;
            const cornerY = isHorizontal1 ? p1y : p2y;
            
            // Approach corner
            if (isHorizontal1) {
                path += ` L ${cornerX - cornerRadius * Math.sign(cornerX - p1x)} ${p1y}`;
            } else {
                path += ` L ${p1x} ${cornerY - cornerRadius * Math.sign(cornerY - p1y)}`;
            }
            
            // Corner curve
            path += ` Q ${cornerX} ${cornerY}, `;
            
            // Leave corner
            if (isHorizontal1) {
                path += `${cornerX} ${cornerY + cornerRadius * Math.sign(p2y - cornerY)}`;
            } else {
                path += `${cornerX + cornerRadius * Math.sign(p2x - cornerX)} ${cornerY}`;
            }
        }
        
        path += ` L ${p2x} ${p2y} L ${x2} ${y2}`;
        return path;
    }
    
    // CURVY (default): Smooth bezier curves
    const straightDist = debugConfig?.cableStraightDist || 10;
    
    // End of straight segments (where curve begins/ends)
    const p1x = x1 + dir1.dx * straightDist;
    const p1y = y1 + dir1.dy * straightDist;
    const p2x = x2 + dir2.dx * straightDist;
    const p2y = y2 + dir2.dy * straightDist;
    
    // Calculate bezier control point distance based on overall path length
    const dist = Math.hypot(p2x - p1x, p2y - p1y);
    const tension = Math.max(30, Math.min(dist * 0.5, 120));
    
    // Control points extend further in the direction of travel
    const cp1x = p1x + dir1.dx * tension;
    const cp1y = p1y + dir1.dy * tension;
    const cp2x = p2x + dir2.dx * tension;
    const cp2y = p2y + dir2.dy * tension;
    
    // Path: start -> straight segment -> curve -> straight segment -> end
    return `M ${x1} ${y1} L ${p1x} ${p1y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2x} ${p2y} L ${x2} ${y2}`;
}

/**
 * Determine best approach side when dragging to cursor (no target port)
 */
export function inferApproachSide(fromX, fromY, toX, toY, fromSide) {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    
    // Prefer approaching from the opposite axis of the source
    // (horizontal source -> vertical approach, vertical source -> horizontal approach)
    if (fromSide === 'left' || fromSide === 'right') {
        // Source is horizontal, approach vertically if cursor is more vertical
        if (absDy > absDx * 0.5) {
            return dy > 0 ? 'top' : 'bottom';
        }
        // Otherwise approach horizontally from opposite side
        return dx > 0 ? 'left' : 'right';
    } else {
        // Source is vertical, approach horizontally if cursor is more horizontal
        if (absDx > absDy * 0.5) {
            return dx > 0 ? 'left' : 'right';
        }
        // Otherwise approach vertically from opposite side
        return dy > 0 ? 'top' : 'bottom';
    }
}

// ============= PORT POSITION =============

/**
 * Get canvas position for a port connector
 */
export function getPortPosition(deviceId, groupId, direction, index) {
    const device = document.getElementById(deviceId);
    if (!device) return null;

    const selector = `.port[data-device="${deviceId}"][data-group="${groupId}"][data-direction="${direction}"][data-index="${index}"]`;
    const port = device.querySelector(selector);
    if (!port) return null;

    const connector = port.querySelector('.port-connector');
    if (!connector) return null;
    
    // Get the port's side from data attribute (default based on direction for backwards compat)
    const side = port.dataset.side || (direction === 'input' ? 'left' : 'right');
    
    const deviceRect = device.getBoundingClientRect();
    const connectorRect = connector.getBoundingClientRect();
    
    const viewport = getViewportFunc();
    const offsetInDeviceX = (connectorRect.left + connectorRect.width/2 - deviceRect.left) / viewport.scale;
    const offsetInDeviceY = (connectorRect.top + connectorRect.height/2 - deviceRect.top) / viewport.scale;
    
    const deviceData = instanceModel.getById(deviceId);
    if (!deviceData) return null;
    
    return {
        x: deviceData.position.x + offsetInDeviceX,
        y: deviceData.position.y + offsetInDeviceY,
        side: side
    };
}

/**
 * Get position of a folded group connector
 */
export function getFoldedGroupPosition(deviceId, groupId, direction, foldKey = null) {
    const device = document.getElementById(deviceId);
    if (!device) return null;

    // Try to find by foldKey first (for subgroups), then fall back to groupId
    let foldedEl = null;
    if (foldKey) {
        const keySelector = `.port-group-folded[data-device="${deviceId}"][data-fold-key="${foldKey}"][data-direction="${direction}"]`;
        foldedEl = device.querySelector(keySelector);
    }
    if (!foldedEl) {
        const selector = `.port-group-folded[data-device="${deviceId}"][data-group="${groupId}"][data-direction="${direction}"]`;
        foldedEl = device.querySelector(selector);
    }
    if (!foldedEl) return null;

    const connector = foldedEl.querySelector('.folded-connector');
    if (!connector) return null;
    
    const side = foldedEl.dataset.side || (direction === 'input' ? 'left' : 'right');
    
    const deviceRect = device.getBoundingClientRect();
    const connectorRect = connector.getBoundingClientRect();
    
    const viewport = getViewportFunc();
    const offsetInDeviceX = (connectorRect.left + connectorRect.width/2 - deviceRect.left) / viewport.scale;
    const offsetInDeviceY = (connectorRect.top + connectorRect.height/2 - deviceRect.top) / viewport.scale;
    
    const deviceData = instanceModel.getById(deviceId);
    if (!deviceData) return null;
    
    return {
        x: deviceData.position.x + offsetInDeviceX,
        y: deviceData.position.y + offsetInDeviceY,
        side: side
    };
}

// ============= RENDERING =============

/**
 * iOS Safari SVG repaint fix - force layer invalidation
 * Safari/WebKit has bugs with SVG rendering when CSS transforms are applied.
 * Toggling an attribute forces the browser to recalculate the SVG layer.
 */
function forceIOSRepaint() {
    // Toggle a benign attribute - this is lightweight and safe on all browsers
    const current = cablesSvg.getAttribute('data-repaint') || '0';
    cablesSvg.setAttribute('data-repaint', current === '0' ? '1' : '0');
}

/**
 * Render all cables to the SVG element
 */
export function renderAllCables() {
    // Clear external cables from global SVG
    cablesSvg.querySelectorAll('path:not(#temp-cable)').forEach(p => p.remove());
    
    // Clear internal cables from all device SVGs
    document.querySelectorAll('.device-internal-cables').forEach(svg => {
        svg.querySelectorAll('path').forEach(p => p.remove());
    });
    
    // Track bundled cables for folded groups
    const bundledCables = new Map(); // key: "srcDeviceId:srcGroupId:tgtDeviceId:tgtGroupId" -> {conns, sourcePos, targetPos}
    
    // Track cable indices per source group for shade variation
    // Key: "deviceId:groupIndex" -> count of cables rendered from this group
    const groupCableIndices = new Map();

    connectionModel.forEach(conn => {
        const sourceDevice = instanceModel.getById(conn.sourceDeviceId);
        const targetDevice = instanceModel.getById(conn.targetDeviceId);
        
        if (!sourceDevice || !targetDevice) return;
        
        // Resolve group indices (handles both new and legacy format)
        const sourceGroupIndex = getConnectionSourceGroup(conn);
        const targetGroupIndex = getConnectionTargetGroup(conn);
        
        const sourceFolded = isPortFolded(sourceDevice, sourceGroupIndex, conn.sourcePortIndex);
        const targetFolded = isPortFolded(targetDevice, targetGroupIndex, conn.targetPortIndex);
        
        // Check for custom colors
        const sourceColor = getGroupColorFromDevice(sourceDevice, sourceGroupIndex);
        const targetColor = getGroupColorFromDevice(targetDevice, targetGroupIndex);
        // Source color takes precedence, then target color
        const customColor = sourceColor || targetColor;
        
        // Get positions (for folded groups, get the folded connector position)
        // Use actual direction from connection (insert ports stored their direction)
        const sourceDir = conn.sourceDirection || 'output';
        const targetDir = conn.targetDirection || 'input';
        let sourcePos, targetPos;
        
        if (sourceFolded) {
            const sourceFoldKey = getFoldKeyForPort(sourceDevice, sourceGroupIndex, conn.sourcePortIndex);
            sourcePos = getFoldedGroupPosition(conn.sourceDeviceId, sourceGroupIndex, sourceDir, sourceFoldKey);
        } else {
            sourcePos = getPortPosition(conn.sourceDeviceId, sourceGroupIndex, sourceDir, conn.sourcePortIndex);
        }
        
        if (targetFolded) {
            const targetFoldKey = getFoldKeyForPort(targetDevice, targetGroupIndex, conn.targetPortIndex);
            targetPos = getFoldedGroupPosition(conn.targetDeviceId, targetGroupIndex, targetDir, targetFoldKey);
        } else {
            targetPos = getPortPosition(conn.targetDeviceId, targetGroupIndex, targetDir, conn.targetPortIndex);
        }

        if (!sourcePos || !targetPos) return;
        
        // Check if this is internal routing (same device)
        const isInternalRouting = conn.sourceDeviceId === conn.targetDeviceId;
        
        // Only bundle cables when BOTH ends are folded
        // When only one side is folded, render individual cables from each unfolded port
        // converging to/from the single folded connector
        if (sourceFolded && targetFolded) {
            const sourceFoldKey = getFoldKeyForPort(sourceDevice, sourceGroupIndex, conn.sourcePortIndex);
            const targetFoldKey = getFoldKeyForPort(targetDevice, targetGroupIndex, conn.targetPortIndex);
            const bundleKey = `${conn.sourceDeviceId}:${sourceFoldKey}:${conn.targetDeviceId}:${targetFoldKey}`;
            
            if (!bundledCables.has(bundleKey)) {
                bundledCables.set(bundleKey, {
                    conns: [],
                    sourcePos,
                    targetPos,
                    sourceType: conn.sourcePortType,
                    customColor,
                    isInternal: isInternalRouting,
                    deviceId: isInternalRouting ? conn.sourceDeviceId : null
                });
            }
            bundledCables.get(bundleKey).conns.push(conn);
            return; // Don't render individual cable
        }

        // Regular cable (including when only one side is folded)
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        
        // For internal routing (same device), flip approach sides so cable curves inward
        let sourceSide = sourcePos.side;
        let targetSide = targetPos.side;
        if (isInternalRouting) {
            const flipSide = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
            sourceSide = flipSide[sourcePos.side] || sourcePos.side;
            targetSide = flipSide[targetPos.side] || targetPos.side;
        }
        
        // For internal cables, calculate coordinates relative to device position
        let x1 = sourcePos.x, y1 = sourcePos.y, x2 = targetPos.x, y2 = targetPos.y;
        if (isInternalRouting) {
            // Convert to device-relative coordinates
            x1 -= sourceDevice.position.x;
            y1 -= sourceDevice.position.y;
            x2 -= sourceDevice.position.x;
            y2 -= sourceDevice.position.y;
        }
        
        path.setAttribute('d', createCablePath(x1, y1, x2, y2, sourceSide, targetSide));
        
        // Check if this is an insert-to-insert (bidirectional) connection
        const isBidirectional = conn.sourceDirection === 'insert' && conn.targetDirection === 'insert';
        if (isBidirectional) {
            // Bidirectional insert cable - use special styling
            path.className.baseVal = `cable-${conn.sourcePortType} cable-insert-bidi`;
        } else if (isInternalRouting) {
            // Internal routing cable - add internal class for distinctive styling
            path.className.baseVal = `cable-${conn.sourcePortType} cable-internal`;
        } else if (conn.sourceDirection === 'insert' || conn.targetDirection === 'insert') {
            // Single insert involved - still use the signal type color
            path.className.baseVal = `cable-${conn.sourcePortType}`;
        } else {
            path.className.baseVal = `cable-${conn.sourcePortType}`;
        }
        path.dataset.id = conn.id;
        path.style.pointerEvents = 'stroke';
        path.style.fill = 'none'; // Ensure no fill (SVG default is black fill)
        
        // Apply shade variation for visual distinction between cables in the same group
        // Track cable index within source group for shade variation
        const groupKey = `${conn.sourceDeviceId}:${sourceGroupIndex}`;
        const cableIndex = groupCableIndices.get(groupKey) || 0;
        groupCableIndices.set(groupKey, cableIndex + 1);
        
        // Use custom color if set, otherwise use default color for the signal type
        const baseColor = customColor || DEFAULT_CABLE_COLORS[conn.sourcePortType] || '#00d9ff';
        
        // Apply subtle shade variation based on cable index
        path.style.stroke = varyShade(baseColor, cableIndex);
        
        path.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent device selection when clicking cable
            selectConnection(conn.id);
        });
        
        // Internal cables go to device's internal SVG, external cables to global SVG
        if (isInternalRouting) {
            const deviceEl = document.getElementById(conn.sourceDeviceId);
            const internalSvg = deviceEl?.querySelector('.device-internal-cables');
            if (internalSvg) {
                // Add invisible wider hit area for easier clicking
                const hitArea = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                hitArea.setAttribute('d', path.getAttribute('d'));
                hitArea.className.baseVal = 'cable-hit-area';
                hitArea.style.stroke = 'transparent';
                hitArea.style.strokeWidth = '16px';
                hitArea.style.fill = 'none';
                hitArea.style.pointerEvents = 'stroke';
                hitArea.style.cursor = 'pointer';
                hitArea.addEventListener('click', (e) => {
                    e.stopPropagation();
                    selectConnection(conn.id);
                });
                internalSvg.appendChild(hitArea);
                internalSvg.appendChild(path);
            } else {
                // Fallback to global SVG if device not found
                cablesSvg.appendChild(path);
            }
        } else {
            cablesSvg.appendChild(path);
        }
    });
    
    // Render bundled cables
    bundledCables.forEach((bundle, key) => {
        const { conns, sourcePos, targetPos, sourceType, customColor, isInternal, deviceId } = bundle;
        
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        
        // For internal routing (same device), flip approach sides so cable curves inward
        const isInternalRouting = isInternal || (conns.length > 0 && conns[0].sourceDeviceId === conns[0].targetDeviceId);
        let sourceSide = sourcePos.side;
        let targetSide = targetPos.side;
        if (isInternalRouting) {
            const flipSide = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
            sourceSide = flipSide[sourcePos.side] || sourcePos.side;
            targetSide = flipSide[targetPos.side] || targetPos.side;
        }
        
        // For internal cables, calculate coordinates relative to device position
        let x1 = sourcePos.x, y1 = sourcePos.y, x2 = targetPos.x, y2 = targetPos.y;
        if (isInternalRouting && deviceId) {
            const device = instanceModel.getById(deviceId);
            if (device) {
                x1 -= device.position.x;
                y1 -= device.position.y;
                x2 -= device.position.x;
                y2 -= device.position.y;
            }
        }
        
        path.setAttribute('d', createCablePath(x1, y1, x2, y2, sourceSide, targetSide));
        path.className.baseVal = `cable-${sourceType} cable-bundled${isInternalRouting ? ' cable-internal' : ''}`;
        path.dataset.bundleKey = key;
        path.dataset.bundleCount = conns.length;
        path.style.pointerEvents = 'stroke';
        
        // Apply custom color if set
        if (customColor) {
            path.style.stroke = customColor;
        }
        
        // Thicker stroke based on bundle count (min 5, max 10)
        const bundleWidth = Math.min(10, 5 + Math.floor(conns.length / 2));
        path.style.strokeWidth = bundleWidth + 'px';
        
        // Click on bundled cable selects all connections in the bundle
        const bundleClickHandler = (e) => {
            e.stopPropagation(); // Prevent device selection when clicking cable
            if (conns.length > 0) {
                selectConnection(conns[0].id);
                showToast(`Bundle: ${conns.length} connections`);
            }
        };
        path.addEventListener('click', bundleClickHandler);
        
        // Add title tooltip
        path.setAttribute('title', `${conns.length} bundled connections`);
        
        // Internal cables go to device's internal SVG, external cables to global SVG
        if (isInternalRouting && deviceId) {
            const deviceEl = document.getElementById(deviceId);
            const internalSvg = deviceEl?.querySelector('.device-internal-cables');
            if (internalSvg) {
                // Add invisible wider hit area for easier clicking
                const hitArea = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                hitArea.setAttribute('d', path.getAttribute('d'));
                hitArea.className.baseVal = 'cable-hit-area';
                hitArea.style.stroke = 'transparent';
                hitArea.style.strokeWidth = '20px'; // Wider for bundled cables
                hitArea.style.fill = 'none';
                hitArea.style.pointerEvents = 'stroke';
                hitArea.style.cursor = 'pointer';
                hitArea.addEventListener('click', bundleClickHandler);
                internalSvg.appendChild(hitArea);
                internalSvg.appendChild(path);
            } else {
                cablesSvg.appendChild(path);
            }
        } else {
            cablesSvg.appendChild(path);
        }
    });
    
    // iOS Safari SVG repaint fix - force layer invalidation
    // Toggling a benign attribute forces WebKit to recalculate SVG rendering
    forceIOSRepaint();
}

// ============= SELECTION =============

/**
 * Select a connection (cable) by ID
 */
export function selectConnection(connectionId) {
    // Clear device selection when selecting a connection
    if (clearDeviceSelectionFunc) {
        clearDeviceSelectionFunc();
    }
    
    state.selectedConnectionId = connectionId;
    state.selectedTemplateId = null;
    
    // Apply selection visual to connection - check both global and internal cable SVGs
    document.querySelectorAll('#cables-svg path').forEach(p => p.classList.remove('selected'));
    document.querySelectorAll('.device-internal-cables path').forEach(p => p.classList.remove('selected'));
    
    if (connectionId) {
        // Try global SVG first, then internal SVGs
        const globalCable = document.querySelector(`#cables-svg path[data-id="${connectionId}"]`);
        if (globalCable) {
            globalCable.classList.add('selected');
        } else {
            // Check internal cable SVGs
            document.querySelector(`.device-internal-cables path[data-id="${connectionId}"]`)?.classList.add('selected');
        }
    }
    
    if (updateSelectionButtonsFunc) {
        updateSelectionButtonsFunc();
    }
    if (renderLibraryFunc) {
        renderLibraryFunc();
    }
}

// ============= CONNECTED STATE =============

/**
 * Update visual state of connected ports
 */
export function updateConnectedPorts() {
    document.querySelectorAll('.port-connector').forEach(c => c.classList.remove('connected'));
    document.querySelectorAll('.port-group-folded').forEach(f => f.classList.remove('connected'));
    
    // Track which folded groups have connections
    const connectedFoldedGroups = new Set();

    connectionModel.forEach(conn => {
        const sourceDevice = document.getElementById(conn.sourceDeviceId);
        const targetDevice = document.getElementById(conn.targetDeviceId);
        const sourceDeviceData = instanceModel.getById(conn.sourceDeviceId);
        const targetDeviceData = instanceModel.getById(conn.targetDeviceId);
        
        // Resolve group indices (handles both new and legacy format)
        const sourceGroupIndex = getConnectionSourceGroup(conn);
        const targetGroupIndex = getConnectionTargetGroup(conn);
        
        // Use actual direction from connection (insert ports store their direction)
        const sourceDir = conn.sourceDirection || 'output';
        const targetDir = conn.targetDirection || 'input';
        
        if (sourceDevice && sourceDeviceData) {
            // Check if source port's subgroup is folded
            if (isPortFolded(sourceDeviceData, sourceGroupIndex, conn.sourcePortIndex)) {
                const foldKey = getFoldKeyForPort(sourceDeviceData, sourceGroupIndex, conn.sourcePortIndex);
                connectedFoldedGroups.add(`${conn.sourceDeviceId}:${foldKey}:${sourceDir}`);
            } else {
                const sourcePort = sourceDevice.querySelector(
                    `.port[data-group="${sourceGroupIndex}"][data-direction="${sourceDir}"][data-index="${conn.sourcePortIndex}"] .port-connector`
                );
                sourcePort?.classList.add('connected');
                
                // Apply custom color to connected state
                const customColor = getGroupColorFromDevice(sourceDeviceData, sourceGroupIndex);
                if (customColor && sourcePort) {
                    const polygon = sourcePort.querySelector('polygon');
                    if (polygon) {
                        polygon.style.fill = customColor;
                        polygon.style.stroke = customColor;
                    }
                }
            }
        }
        
        if (targetDevice && targetDeviceData) {
            // Check if target port's subgroup is folded
            if (isPortFolded(targetDeviceData, targetGroupIndex, conn.targetPortIndex)) {
                const foldKey = getFoldKeyForPort(targetDeviceData, targetGroupIndex, conn.targetPortIndex);
                connectedFoldedGroups.add(`${conn.targetDeviceId}:${foldKey}:${targetDir}`);
            } else {
                const targetPort = targetDevice.querySelector(
                    `.port[data-group="${targetGroupIndex}"][data-direction="${targetDir}"][data-index="${conn.targetPortIndex}"] .port-connector`
                );
                targetPort?.classList.add('connected');
                
                // Apply custom color to connected state
                const customColor = getGroupColorFromDevice(targetDeviceData, targetGroupIndex);
                if (customColor && targetPort) {
                    const polygon = targetPort.querySelector('polygon');
                    if (polygon) {
                        polygon.style.fill = customColor;
                        polygon.style.stroke = customColor;
                    }
                }
            }
        }
    });
    
    // Mark folded groups as connected
    connectedFoldedGroups.forEach(key => {
        // Key format: "deviceId:foldKey:direction" where foldKey may contain ":"
        const parts = key.split(':');
        const deviceId = parts[0];
        const direction = parts[parts.length - 1];
        const foldKey = parts.slice(1, -1).join(':');
        
        const deviceEl = document.getElementById(deviceId);
        if (deviceEl) {
            // Try to find by foldKey first (for subgroups)
            let foldedEl = deviceEl.querySelector(
                `.port-group-folded[data-fold-key="${foldKey}"][data-direction="${direction}"]`
            );
            // Fall back to group id (for non-split groups)
            if (!foldedEl) {
                foldedEl = deviceEl.querySelector(
                    `.port-group-folded[data-group="${foldKey}"][data-direction="${direction}"]`
                );
            }
            foldedEl?.classList.add('connected');
        }
    });
}
